import { create } from 'zustand';

/**
 * screenLockStore (screen-lock — 2026-08-19) — 화면잠금의 설정과 잠금 상태.
 *
 * 사용자 요구(2026-08-19): "세션 시간이 지나면 전체 화면을 가리고 중앙에 비밀번호를 입력하면
 * 해제. 대기시간 기본 1시간. 그 이후는 로그아웃."
 *
 * 두 가지를 나눠 들고 있다.
 *   - **설정**(settings): 켜짐 여부·디자인·대기 시간·자동 로그아웃 시간·잠금 예고.
 *     기기별로 저장한다(localStorage) — 잠금은 "이 자리를 비웠는가" 의 문제라 계정이 아니라
 *     쓰는 기기에 매인다. 계정별 저장·관리자 강제는 2단계(서버 컬럼 필요).
 *   - **잠금 상태**(lockedAt): 언제 잠겼는가. 이것도 localStorage 에 남긴다 —
 *     남기지 않으면 새로고침(F5)이나 탭 복제로 잠금이 풀려 잠근 의미가 없다.
 *   - **연속 실패 횟수**(fails): 같은 이유로 남긴다(2026-08-28). 메모리에만 두면
 *     **새로고침 한 번에 0 으로 돌아가** 허용 횟수를 정한 뜻이 사라졌다.
 *
 * 탭 사이는 BroadcastChannel + storage 이벤트로 맞춘다(authStore 와 같은 방식). 한 탭이 잠기면
 * 전부 잠기고, 한 곳에서 풀면 전부 풀린다.
 */

/** 가림막 종류 — 시안 A/B/C. */
export type LockDesign = 'blur' | 'cover' | 'sheet';

export const LOCK_DESIGNS: readonly LockDesign[] = ['blur', 'cover', 'sheet'];

/** 잠금까지 대기 시간 선택지(분). */
export const IDLE_MINUTE_OPTIONS: readonly number[] = [5, 10, 30, 60, 120, 240];
/** 잠긴 뒤 자동 로그아웃 선택지(분). 0 = 사용 안 함. */
export const LOGOUT_MINUTE_OPTIONS: readonly number[] = [30, 60, 120, 0];

/** 잠금 예고를 띄우는 시점(초) — 잠기기 5분 전. */
export const PREWARN_SEC = 5 * 60;

/**
 * 비밀번호 연속 실패 허용 횟수 선택지 — 넘으면 로그아웃(로그인 화면으로).
 *
 * ★ 2026-08-28 에 **설정값으로 바꿨다**(전에는 코드에 박힌 5). 「1회」는 두지 않는다 —
 *   한 번의 오타로 하던 일이 끊기는 것이 바로 이 기능을 고치게 만든 불만이었다.
 */
export const FAIL_LIMIT_OPTIONS: readonly number[] = [3, 5, 10];

export interface ScreenLockSettings {
  enabled: boolean;
  design: LockDesign;
  /** 잠금까지 무활동 대기(분). */
  idleMinutes: number;
  /** 잠긴 뒤 자동 로그아웃까지(분). 0 이면 자동 로그아웃 안 함. */
  logoutMinutes: number;
  /** 잠기기 5분 전 알림. */
  prewarn: boolean;
  /** 비밀번호를 몇 번까지 틀려도 되는가. 넘으면 로그아웃. */
  maxFails: number;
}

/**
 * 확정 기본값(2026-08-19 사용자 확정): 켜짐 · A(흐림) · 1시간 · 로그아웃 60분.
 * 실패 허용 **3회**(2026-08-28 사용자 확정).
 */
export const DEFAULT_SETTINGS: ScreenLockSettings = {
  enabled: true,
  design: 'blur',
  idleMinutes: 60,
  logoutMinutes: 60,
  prewarn: true,
  maxFails: 3,
};

const SETTINGS_KEY = 'axi.screenLock.settings';
const LOCKED_AT_KEY = 'axi.screenLock.lockedAt';
const FAILS_KEY = 'axi.screenLock.fails';
const CHANNEL_NAME = 'axi.screen-lock';

const isBrowser = typeof window !== 'undefined';

type LockMsg =
  | { type: 'lock'; at: number }
  | { type: 'unlock' }
  | { type: 'settings'; settings: ScreenLockSettings };

const channel: BroadcastChannel | null =
  isBrowser && typeof BroadcastChannel !== 'undefined' ? new BroadcastChannel(CHANNEL_NAME) : null;

function readSettings(): ScreenLockSettings {
  if (!isBrowser) return DEFAULT_SETTINGS;
  try {
    const raw = window.localStorage.getItem(SETTINGS_KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<ScreenLockSettings>;
    return normalizeSettings(parsed);
  } catch {
    return DEFAULT_SETTINGS;
  }
}

/** 저장값이 낡거나 손상돼도 화면이 깨지지 않도록 — 모르는 값은 기본값으로 되돌린다. */
export function normalizeSettings(v: Partial<ScreenLockSettings> | null | undefined): ScreenLockSettings {
  const d = DEFAULT_SETTINGS;
  if (!v || typeof v !== 'object') return d;
  return {
    enabled: typeof v.enabled === 'boolean' ? v.enabled : d.enabled,
    design: LOCK_DESIGNS.includes(v.design as LockDesign) ? (v.design as LockDesign) : d.design,
    idleMinutes: IDLE_MINUTE_OPTIONS.includes(Number(v.idleMinutes)) ? Number(v.idleMinutes) : d.idleMinutes,
    logoutMinutes: LOGOUT_MINUTE_OPTIONS.includes(Number(v.logoutMinutes))
      ? Number(v.logoutMinutes)
      : d.logoutMinutes,
    prewarn: typeof v.prewarn === 'boolean' ? v.prewarn : d.prewarn,
    // 옛 저장값에는 이 칸이 없다 — 그때는 기본값(3)이 된다.
    maxFails: FAIL_LIMIT_OPTIONS.includes(Number(v.maxFails)) ? Number(v.maxFails) : d.maxFails,
  };
}

function readLockedAt(): number | null {
  if (!isBrowser) return null;
  try {
    const raw = window.localStorage.getItem(LOCKED_AT_KEY);
    const n = Number(raw);
    return raw && Number.isFinite(n) && n > 0 ? n : null;
  } catch {
    return null;
  }
}

function writeLockedAt(at: number | null): void {
  if (!isBrowser) return;
  try {
    if (at === null) window.localStorage.removeItem(LOCKED_AT_KEY);
    else window.localStorage.setItem(LOCKED_AT_KEY, String(at));
  } catch { /* ignore */ }
}

function readFails(): number {
  if (!isBrowser) return 0;
  try {
    const n = Number(window.localStorage.getItem(FAILS_KEY));
    // 손상값·음수는 0 으로 — 못 읽었다고 사람을 쫓아내지 않는다.
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : 0;
  } catch {
    return 0;
  }
}

function writeFails(n: number): void {
  if (!isBrowser) return;
  try {
    if (n <= 0) window.localStorage.removeItem(FAILS_KEY);
    else window.localStorage.setItem(FAILS_KEY, String(n));
  } catch { /* ignore */ }
}

function post(msg: LockMsg): void {
  channel?.postMessage(msg);
}

interface ScreenLockState {
  settings: ScreenLockSettings;
  /** 잠긴 시각(ms). null 이면 잠기지 않은 상태 — 잠금 여부의 진실원. */
  lockedAt: number | null;
  /** 연속 비밀번호 실패 횟수. */
  fails: number;
  /** 설정 화면의 "미리보기" 로 잠깐 띄운 디자인. 실제 잠금과 구분한다. */
  preview: LockDesign | null;

  lock: () => void;
  unlock: () => void;
  /** 다른 탭이 알려온 상태를 그대로 반영(재방송 없음 — 메아리 차단). */
  applyRemote: (msg: LockMsg) => void;
  patchSettings: (patch: Partial<ScreenLockSettings>) => void;
  bumpFail: () => number;
  resetFails: () => void;
  setPreview: (design: LockDesign | null) => void;
  /** 저장소에서 다시 읽어온다(마운트·storage 이벤트). */
  hydrate: () => void;
}

export const useScreenLockStore = create<ScreenLockState>((set, get) => ({
  settings: readSettings(),
  lockedAt: readLockedAt(),
  fails: readFails(),
  preview: null,

  lock: () => {
    if (get().lockedAt !== null) return;
    const at = Date.now();
    writeLockedAt(at);
    writeFails(0);            // 새로 잠글 때마다 셈은 처음부터
    set({ lockedAt: at, fails: 0, preview: null });
    post({ type: 'lock', at });
  },

  unlock: () => {
    writeLockedAt(null);
    writeFails(0);
    set({ lockedAt: null, fails: 0 });
    post({ type: 'unlock' });
  },

  applyRemote: (msg) => {
    // 다른 탭이 잠그거나 풀었으면 저장소는 그 탭이 이미 손봤다 — 여기서는 상태만 맞춘다.
    if (msg.type === 'lock') set({ lockedAt: msg.at, fails: 0, preview: null });
    else if (msg.type === 'unlock') set({ lockedAt: null, fails: 0 });
    else if (msg.type === 'settings') set({ settings: normalizeSettings(msg.settings) });
  },

  patchSettings: (patch) => {
    const next = normalizeSettings({ ...get().settings, ...patch });
    if (isBrowser) {
      try { window.localStorage.setItem(SETTINGS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
    }
    set({ settings: next });
    post({ type: 'settings', settings: next });
  },

  bumpFail: () => {
    const n = get().fails + 1;
    writeFails(n);            // ★ 새로고침해도 셈이 남는다 — 그래야 허용 횟수가 뜻을 갖는다
    set({ fails: n });
    return n;
  },

  resetFails: () => { writeFails(0); set({ fails: 0 }); },

  setPreview: (design) => set({ preview: design }),

  hydrate: () => set({ settings: readSettings(), lockedAt: readLockedAt(), fails: readFails() }),
}));

/**
 * 탭 간 동기화 구독 — 호스트가 마운트 때 한 번 건다.
 * BroadcastChannel 이 없는 브라우저를 위해 storage 이벤트도 같이 듣는다(잠금 키만 본다).
 */
export function subscribeScreenLockSync(): () => void {
  if (!isBrowser) return () => { /* noop */ };
  const onMessage = (e: MessageEvent<LockMsg>) => {
    if (e.data && typeof e.data === 'object') useScreenLockStore.getState().applyRemote(e.data);
  };
  const onStorage = (e: StorageEvent) => {
    if (e.key === LOCKED_AT_KEY || e.key === SETTINGS_KEY || e.key === FAILS_KEY || e.key === null) {
      useScreenLockStore.getState().hydrate();
    }
  };
  channel?.addEventListener('message', onMessage as EventListener);
  window.addEventListener('storage', onStorage);
  return () => {
    channel?.removeEventListener('message', onMessage as EventListener);
    window.removeEventListener('storage', onStorage);
  };
}

/** 자동 로그아웃 시각(ms). logoutMinutes=0 이거나 잠기지 않았으면 null. */
export function logoutDeadline(lockedAt: number | null, logoutMinutes: number): number | null {
  if (lockedAt === null || logoutMinutes <= 0) return null;
  return lockedAt + logoutMinutes * 60_000;
}

/** 테스트 전용 — 저장소·상태 초기화. */
export function _resetScreenLockForTests(): void {
  if (isBrowser) {
    try {
      window.localStorage.removeItem(SETTINGS_KEY);
      window.localStorage.removeItem(LOCKED_AT_KEY);
      window.localStorage.removeItem(FAILS_KEY);
    } catch { /* ignore */ }
  }
  useScreenLockStore.setState({
    settings: DEFAULT_SETTINGS, lockedAt: null, fails: 0, preview: null,
  });
}
